import React, { useEffect, useRef, useState } from "react";
import { HeartPulse, Volume2, VolumeX, Activity, PhoneCall, Zap } from "lucide-react";
import {
    speak,
    speakBurst,
    cancel as cancelSpeech,
    isMuted,
    setMuted,
} from "../lib/speech";
import { useI18n } from "../lib/i18n";
import { useQr } from "../lib/qrContext";

// --- ERC 30:2 timing -------------------------------------------------------
// 110 BPM compressions; per cycle: 30 (real) / 15 (demo) compressions, then
// 2 rescue breaths. Per user request: each rescue breath now lasts 1 s
// longer (2500 ms).
const BPM = 110;
const COMPRESSION_MS = 60000 / BPM; // ≈ 545.45 ms
const BREATH_MS = 2500;
const COMPRESSIONS_PER_CYCLE_REAL = 30;
const COMPRESSIONS_PER_CYCLE_DEMO = 15;
const BREATHS_PER_CYCLE = 2;

const buildCycle = (compressionsCount) => {
    const events = [];
    let t = 0;
    for (let i = 0; i < compressionsCount; i += 1) {
        events.push({ type: "compress", index: i + 1, at: t });
        t += COMPRESSION_MS;
    }
    for (let i = 0; i < BREATHS_PER_CYCLE; i += 1) {
        events.push({ type: "breathe", index: i + 1, at: t });
        t += BREATH_MS;
    }
    return events;
};

const BREADCRUMB_KEYS = {
    "consciousness:no": "breadcrumb_conscious_no",
    "consciousness:yes": "breadcrumb_conscious_yes",
    "breathing:no": "breadcrumb_breathing_no",
    "breathing:yes": "breadcrumb_breathing_yes",
    "called112": "breadcrumb_called_112",
};

const fmtTime = (ms) => {
    const total = Math.floor(ms / 1000);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
};

export const CprGuidance = ({ mode, decisions = [] }) => {
    const { t, lang } = useI18n();
    const { custom } = useQr();
    const isDemo = mode === "demo";
    // Stages: "waiting" → "intro" (announcement) → "active" (rhythm)
    const [stage, setStage] = useState(isDemo ? "intro" : "waiting");
    const [muted, setMutedState] = useState(isMuted());
    const [phase, setPhase] = useState({ type: "compress", index: 0 });
    const [elapsedMs, setElapsedMs] = useState(0);

    const rafRef = useRef(null);
    const startRef = useRef(0);
    const nextEventIdxRef = useRef(0);
    const cycleCountRef = useRef(0);
    const audioCtxRef = useRef(null);
    const milestoneRef = useRef({ nextHandover: 120_000 });
    // While > now, don't speak "Beatmen" so an active reminder is
    // clearly audible; the beeps continue so the rhythm stays palpable.
    const suppressVoiceUntilRef = useRef(0);

    // --- Web Audio beep (precise, low-latency) ---
    const beep = (freq = 880, duration = 0.1, volume = 0.35) => {
        if (muted) return;
        if (!audioCtxRef.current) {
            try {
                audioCtxRef.current = new (window.AudioContext ||
                    window.webkitAudioContext)();
            } catch (_) {
                return;
            }
        }
        const ctx = audioCtxRef.current;
        if (ctx.state === "suspended") ctx.resume();
        const now = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, now);
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(volume, now + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + duration + 0.02);
    };

    // Breath sound: distinct two-tone "inhale-exhale" within the 2.5s window.
    const breathSound = () => {
        beep(659, 0.3, 0.32);
        setTimeout(() => beep(440, 0.45, 0.3), 380);
    };

    // Approximate speech duration based on character count at our default rate.
    const estimateSpeechMs = (text) => Math.max(1500, text.length * 70);

    // Trigger a milestone announcement: cancels in-flight Drücken/Beatmen
    // chants, suppresses them for the spoken duration + 5 s grace, but lets
    // the rhythm beeps continue uninterrupted.
    const fireReminder = (text) => {
        const dur = estimateSpeechMs(text);
        suppressVoiceUntilRef.current = performance.now() + dur + 5000;
        speakBurst(text, { rate: 0.9 });
    };

    // --- Visibility detection: on real-mode 112, wait for return ---
    useEffect(() => {
        if (isDemo || stage !== "waiting") return;
        const onVisibility = () => {
            if (document.visibilityState === "visible") setStage("intro");
        };
        document.addEventListener("visibilitychange", onVisibility);
        // Grace timeout: 4 s after mount, if visible (e.g. desktop with no
        // dialer focus loss), proceed automatically.
        const grace = setTimeout(() => {
            if (document.visibilityState === "visible") setStage("intro");
        }, 4000);
        return () => {
            document.removeEventListener("visibilitychange", onVisibility);
            clearTimeout(grace);
        };
    }, [isDemo, stage]);

    // --- Intro announcement; on end → start the rhythm ---
    useEffect(() => {
        if (stage !== "intro") return;
        if (muted) {
            setStage("active");
            return;
        }
        const base = t("cpr_intro_tts");
        const text = isDemo ? `${t("demo_tts_prefix")} ${base}` : base;
        speak(text, {
            onEnd: () => setStage("active"),
        });
        return () => cancelSpeech();
    }, [stage, muted, lang, t, isDemo]);

    // --- Main rhythm loop ---
    const COMPRESSIONS_PER_CYCLE = isDemo
        ? COMPRESSIONS_PER_CYCLE_DEMO
        : COMPRESSIONS_PER_CYCLE_REAL;
    const CYCLE_EVENTS = React.useMemo(
        () => buildCycle(COMPRESSIONS_PER_CYCLE),
        [COMPRESSIONS_PER_CYCLE]
    );
    const CYCLE_MS =
        COMPRESSIONS_PER_CYCLE * COMPRESSION_MS +
        BREATHS_PER_CYCLE * BREATH_MS;

    // Email-capture popup is now rendered globally at the App level
    // (10 s after page load). CPR no longer pauses for it.

    useEffect(() => {
        if (stage !== "active") return;

        startRef.current = performance.now();
        nextEventIdxRef.current = 0;
        cycleCountRef.current = 0;
        milestoneRef.current = { nextHandover: 120_000 };
        suppressVoiceUntilRef.current = 0;
        setPhase({ type: "compress", index: 0 });
        setElapsedMs(0);

        const fire = (ev) => {
            const elapsedNow = performance.now() - startRef.current;
            const voiceAllowed =
                elapsedNow < 60_000 &&
                performance.now() >= suppressVoiceUntilRef.current;
            if (ev.type === "compress") {
                beep(880, 0.09, 0.35);
                setPhase({ type: "compress", index: ev.index });
            } else {
                breathSound();
                if (voiceAllowed) speakBurst(t("cpr_breath_tts"), { rate: 1.4 });
                setPhase({ type: "breathe", index: ev.index });
            }
        };

        const tick = () => {
            const now = performance.now();
            const elapsed = now - startRef.current;
            setElapsedMs(elapsed);

            if (elapsed >= milestoneRef.current.nextHandover) {
                milestoneRef.current.nextHandover += 120_000;
                fireReminder(t("cpr_handover_tts"));
            }

            // Fire all events whose scheduled time has passed
            while (nextEventIdxRef.current < CYCLE_EVENTS.length) {
                const ev = CYCLE_EVENTS[nextEventIdxRef.current];
                const scheduled =
                    cycleCountRef.current * CYCLE_MS + ev.at;
                if (scheduled <= elapsed) {
                    fire(ev);
                    nextEventIdxRef.current += 1;
                } else {
                    break;
                }
            }
            if (nextEventIdxRef.current >= CYCLE_EVENTS.length) {
                cycleCountRef.current += 1;
                nextEventIdxRef.current = 0;
            }
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            cancelSpeech();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stage]);

    const toggleMute = () => {
        const next = !muted;
        setMuted(next);
        setMutedState(next);
        if (next) cancelSpeech();
    };

    // --- Waiting screen ---
    if (stage === "waiting") {
        return (
            <section
                data-testid="cpr-waiting-section"
                className="bg-white border-2 border-zinc-200 rounded-3xl p-6 sm:p-10 shadow-sm anim-fade-up text-center"
            >
                <Breadcrumb decisions={decisions} center />
                <div className="w-16 h-16 mx-auto rounded-2xl bg-[#A72920] text-white flex items-center justify-center">
                    <PhoneCall className="w-8 h-8 animate-pulse" strokeWidth={2.5} />
                </div>
                <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter mt-4 leading-tight">
                    {t("cpr_waiting_title")}
                </h2>
                <p className="text-base sm:text-lg text-zinc-600 max-w-xl mx-auto mt-3">
                    {t("cpr_waiting_body")}
                </p>
                <button
                    data-testid="cpr-start-now-button"
                    onClick={() => setStage("intro")}
                    className="mt-6 bg-[#245288] hover:bg-[#A72920] text-white font-display font-black text-base px-6 py-3.5 rounded-full transition"
                >
                    {t("cpr_start_now")}
                </button>
            </section>
        );
    }

    // --- Intro screen (announcement playing) ---
    if (stage === "intro") {
        return (
            <section
                data-testid="cpr-intro-section"
                className="bg-white border-2 border-zinc-200 rounded-3xl p-6 sm:p-10 shadow-sm anim-fade-up"
            >
                <Breadcrumb decisions={decisions} />
                <div className="flex items-start justify-between gap-4 mb-6">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#A72920] text-white flex items-center justify-center flex-shrink-0">
                            <HeartPulse className="w-5 h-5" />
                        </div>
                        <h2 className="font-display font-black text-2xl sm:text-4xl tracking-tight leading-tight">
                            {t("cpr_intro_title")}
                        </h2>
                    </div>
                    <button
                        data-testid="tts-mute-toggle"
                        onClick={toggleMute}
                        aria-label={muted ? t("tts_activate") : t("tts_mute")}
                        className="flex-shrink-0 w-11 h-11 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center transition"
                    >
                        {muted ? (
                            <VolumeX className="w-5 h-5 text-zinc-600" />
                        ) : (
                            <Volume2 className="w-5 h-5 text-zinc-900" />
                        )}
                    </button>
                </div>
                <p className="text-lg sm:text-2xl font-display font-bold text-zinc-700 leading-snug">
                    {t("cpr_intro_body")}
                </p>
                <button
                    data-testid="cpr-skip-intro-button"
                    onClick={() => {
                        cancelSpeech();
                        setStage("active");
                    }}
                    className="mt-6 w-full bg-zinc-200 hover:bg-zinc-300 text-zinc-700 font-display font-bold text-base sm:text-lg px-6 py-4 rounded-2xl transition"
                >
                    {t("cpr_start_immediately")}
                </button>
            </section>
        );
    }

    // --- Active metronome screen ---
    const isCompress = phase.type === "compress";
    const counterLabel = isCompress
        ? t("cpr_counter_compressions", { n: phase.index })
        : t("cpr_counter_breaths", { n: phase.index });

    return (
        <>
        <section
            data-testid="cpr-active-section"
            className="bg-white border-2 border-zinc-200 rounded-3xl p-6 sm:p-10 shadow-sm anim-fade-up"
        >
            <Breadcrumb decisions={decisions} />
            {custom?.location && (
                <div
                    data-testid="qr-defib-hint"
                    className="mb-5 rounded-2xl border-2 border-[#A72920]/30 bg-[#A72920]/5 px-4 sm:px-5 py-4 flex items-start gap-3"
                >
                    <div className="w-10 h-10 rounded-xl bg-[#A72920] text-white flex items-center justify-center flex-shrink-0">
                        <Zap className="w-5 h-5" strokeWidth={2.4} />
                    </div>
                    <div className="min-w-0">
                        <div className="text-[11px] sm:text-xs font-display font-bold uppercase tracking-[0.18em] text-[#A72920]">
                            {t("qr_cpr_defib_label")}
                        </div>
                        <div className="font-display font-bold text-lg sm:text-xl text-zinc-900 leading-tight mt-0.5 break-words">
                            {custom.location}
                        </div>
                    </div>
                </div>
            )}
            <div className="flex items-start justify-between gap-4 mb-6">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#A72920] text-white flex items-center justify-center flex-shrink-0">
                        <HeartPulse className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-xs font-display font-bold uppercase tracking-[0.2em] text-zinc-500">
                            {t("cpr_reanimation")}
                            {isDemo && (
                                <span className="ml-2 text-zinc-900 bg-zinc-100 rounded px-1.5 py-0.5">
                                    {t("triage_demo_badge")}
                                </span>
                            )}
                        </div>
                        <h2 className="font-display font-black text-2xl sm:text-4xl tracking-tight mt-1 leading-tight">
                            {t("cpr_active_title")}
                        </h2>
                        <p
                            data-testid="cpr-timer"
                            className="text-sm text-zinc-500 mt-1 font-mono"
                        >
                            ⏱ {fmtTime(elapsedMs)}
                        </p>
                    </div>
                </div>
                <button
                    data-testid="tts-mute-toggle"
                    onClick={toggleMute}
                    aria-label={muted ? "Sprachausgabe aktivieren" : "Sprachausgabe stummschalten"}
                    className="flex-shrink-0 w-11 h-11 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center transition"
                >
                    {muted ? (
                        <VolumeX className="w-5 h-5 text-zinc-600" />
                    ) : (
                        <Volume2 className="w-5 h-5 text-zinc-900" />
                    )}
                </button>
            </div>

            {/* Pulsing metronome circle */}
            <div className="flex flex-col items-center my-4 sm:my-8">
                <div
                    data-testid="cpr-pulse"
                    key={`${phase.type}-${phase.index}`}
                    className={`relative w-48 h-48 sm:w-64 sm:h-64 rounded-full flex items-center justify-center ${
                        isCompress ? "bg-[#A72920]" : "bg-[#245288]"
                    }`}
                    style={{
                        animation: `cpr-pulse ${
                            isCompress ? COMPRESSION_MS : BREATH_MS
                        }ms ease-out`,
                    }}
                >
                    <div className="text-center text-white">
                        <div className="font-display font-black text-5xl sm:text-7xl tracking-tighter leading-none">
                            {isCompress ? t("cpr_label_compress") : t("cpr_label_breathe")}
                        </div>
                        <div className="mt-2 text-xs font-display font-bold uppercase tracking-[0.2em] opacity-80">
                            {counterLabel}
                        </div>
                    </div>
                </div>
                <div className="mt-5 text-xs text-zinc-400 font-mono">
                    {t("cpr_bpm_line")}
                </div>
            </div>

            {/* Progress dots */}
            <div
                className={`grid gap-1.5 my-4 ${
                    COMPRESSIONS_PER_CYCLE <= 15
                        ? "grid-cols-15"
                        : "grid-cols-10"
                }`}
                style={
                    COMPRESSIONS_PER_CYCLE <= 15
                        ? {
                              gridTemplateColumns:
                                  `repeat(${COMPRESSIONS_PER_CYCLE}, minmax(0, 1fr))`,
                          }
                        : undefined
                }
            >
                {Array.from({ length: COMPRESSIONS_PER_CYCLE }).map((_, i) => (
                    <div
                        key={i}
                        data-testid={`cpr-dot-${i}`}
                        className={`h-2 rounded-full transition ${
                            isCompress && phase.index > i
                                ? "bg-[#A72920]"
                                : "bg-zinc-200"
                        }`}
                    />
                ))}
            </div>

            <div className="mt-6 text-xs text-zinc-400 flex items-center gap-2">
                <Activity className="w-3.5 h-3.5" />
                {t("cpr_continue_hint")}
            </div>
        </section>
        </>
    );
};

const Breadcrumb = ({ decisions, center }) => {
    const { t } = useI18n();
    if (!decisions || decisions.length === 0) return null;
    // Once the emergency call has been triggered we only want to surface
    // that fact – prominently, so the responder always sees "Notruf wurde
    // abgesetzt" while performing CPR without the older triage steps
    // cluttering the header.
    if (decisions.includes("called112")) {
        return (
            <div
                data-testid="triage-breadcrumb"
                className={`mb-5 font-display font-black text-2xl sm:text-3xl tracking-tight text-[#c70000] uppercase ${
                    center ? "text-center" : ""
                }`}
            >
                {t("breadcrumb_called_112")}
            </div>
        );
    }
    return (
        <div
            data-testid="triage-breadcrumb"
            className={`mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] sm:text-xs font-display font-bold uppercase tracking-[0.18em] text-zinc-400 ${
                center ? "justify-center" : ""
            }`}
        >
            {decisions.map((d, i) => (
                <React.Fragment key={d}>
                    {i > 0 && <span className="text-zinc-300">/</span>}
                    <span className="text-zinc-600">
                        {BREADCRUMB_KEYS[d] ? t(BREADCRUMB_KEYS[d]) : d}
                    </span>
                </React.Fragment>
            ))}
        </div>
    );
};
